import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const AZUL = [37, 99, 235];
const META_ML = 1600;

const soData = (iso) => (iso || "").split("T")[0];
const hora = (iso) => ((iso || "").split("T")[1] || "").slice(0, 5);
const dataBR = (d) => {
  if (!d) return "-";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
};
const hojeISO = () => new Date().toISOString().slice(0, 10);

function cabecalho(doc, empresa, titulo, sub) {
  doc.setFillColor(...AZUL);
  doc.rect(0, 0, 210, 26, "F");
  let tx = 14;
  if (empresa?.logo) {
    try { doc.addImage(empresa.logo, "PNG", 12, 4, 18, 18); tx = 34; } catch (e) { /* ignore */ }
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(empresa?.nome || "Casa de Repouso São Jorge", tx, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`CNPJ: ${empresa?.cnpj || "-"}`, tx, 18);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(titulo, 196, 12, { align: "right" });
  if (sub) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text(sub, 196, 18, { align: "right" });
  }
  doc.setTextColor(30, 41, 59);
}

function secao(doc, y, titulo) {
  if (y > 268) { doc.addPage(); y = 20; }
  doc.setFillColor(241, 245, 249);
  doc.rect(14, y - 5, 182, 7, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(30, 41, 59);
  doc.text(titulo, 16, y);
  return y + 4;
}

function tabela(doc, y, head, body) {
  autoTable(doc, {
    startY: y,
    head: [head],
    body: body.length ? body : [["—", ...head.slice(1).map(() => "")]],
    theme: "striped",
    headStyles: { fillColor: AZUL, fontSize: 8.5 },
    styles: { fontSize: 8.5, cellPadding: 1.6, valign: "top" },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 8;
}

function paciente(doc, y, residente) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(71, 85, 105);
  doc.text(`Residente: ${residente?.nome || "-"}`, 14, y);
  doc.text(`Quarto: ${residente?.quarto || "-"}`, 120, y);
  if (residente?.cns) doc.text(`CNS: ${residente.cns}`, 150, y);
  return y + 8;
}

export function gerarProntuarioDiaPDF({ empresa, residente, registros, data }) {
  const dia = data || hojeISO();
  const doc = new jsPDF();
  cabecalho(doc, empresa, "PRONTUÁRIO DO DIA", `Passagem de plantão · ${dataBR(dia)}`);
  let y = 34;
  y = paciente(doc, y, residente);

  const doDia = (registros || []).filter((r) => soData(r.data) === dia);
  const porTipo = (t) => doDia.filter((r) => r.tipo === t)
    .sort((a, b) => (a.data || "").localeCompare(b.data || ""));

  // Sinais vitais
  y = secao(doc, y, "Sinais Vitais");
  y = tabela(doc, y, ["Hora", "PA", "FC", "FR", "Temp", "SpO2", "Glicemia", "Dor", "Prof."],
    porTipo("sinais_vitais").map((r) => {
      const d = r.dados || {};
      return [hora(r.data),
        d.pas || d.pad ? `${d.pas || "-"}/${d.pad || "-"}` : "-",
        d.fc || "-", d.fr || "-", d.temp || "-", d.spo2 || "-", d.glicemia || "-", d.dor || "-",
        r.autor || "-"];
    }));

  // Hidratação
  const hid = porTipo("balanco_hidrico");
  const totalMl = hid.filter((r) => (r.dados?.via || "Ingesta") === "Ingesta")
    .reduce((s, r) => s + (parseInt(r.dados?.volume, 10) || 0), 0);
  y = secao(doc, y, `Hidratação — Total: ${totalMl} mL / Meta ${META_ML} mL ${totalMl >= META_ML ? "(atingida)" : ""}`);
  y = tabela(doc, y, ["Hora", "Via", "Descrição", "Volume (mL)", "Prof."],
    hid.map((r) => {
      const d = r.dados || {};
      return [hora(r.data), d.via || "Ingesta", d.tipo || "-", d.volume || "-", r.autor || "-"];
    }));

  // Evacuações
  y = secao(doc, y, "Evacuações");
  y = tabela(doc, y, ["Hora", "Quantidade", "Aspecto", "Observação", "Prof."],
    porTipo("evacuacao").map((r) => {
      const d = r.dados || {};
      return [hora(r.data), d.quantidade || "-", d.aspecto || "-", d.obs || "-", r.autor || "-"];
    }));

  // Diurese
  y = secao(doc, y, "Diurese");
  y = tabela(doc, y, ["Hora", "Registro", "Observação", "Prof."],
    porTipo("diurese").map((r) => {
      const d = r.dados || {};
      return [hora(r.data), d.desprezada === false ? "Registrada" : "Desprezada", d.obs || "-", r.autor || "-"];
    }));

  // Medicação (administrações do dia)
  y = secao(doc, y, "Medicação");
  y = tabela(doc, y, ["Hora reg.", "Horário", "Status", "Motivo", "Prof."],
    porTipo("medicacao_adm").map((r) => {
      const d = r.dados || {};
      const st = d.status === "administrada" ? "Administrada"
        : d.status === "nao_administrada" ? "NÃO administrada" : (d.status || "-");
      return [hora(r.data), d.horario || "-", st, d.motivo || "-", r.autor || "-"];
    }));

  // Anotações de enfermagem + ocorrências
  y = secao(doc, y, "Anotações de Enfermagem");
  const anot = [...porTipo("anotacao_enfermagem"), ...porTipo("ocorrencia")]
    .sort((a, b) => (a.data || "").localeCompare(b.data || ""));
  if (anot.length === 0) {
    doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(148, 163, 184);
    if (y > 275) { doc.addPage(); y = 20; }
    doc.text("Nenhuma anotação registrada no dia.", 16, y); y += 8;
  } else {
    anot.forEach((r) => {
      const d = r.dados || {};
      const titulo = r.tipo === "ocorrencia"
        ? `Ocorrência (${d.gravidade || "-"}) · ${hora(r.data)}`
        : `Turno ${d.turno || "-"} · ${hora(r.data)}`;
      const texto = d.texto || "";
      const linhas = doc.splitTextToSize(texto, 178);
      const bloco = 6 + linhas.length * 4.4 + 5;
      if (y + bloco > 285) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(37, 99, 235);
      doc.text(titulo, 16, y); y += 4.6;
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(51, 65, 85);
      doc.text(linhas, 16, y); y += linhas.length * 4.4 + 1;
      doc.setFontSize(7.5); doc.setTextColor(148, 163, 184);
      doc.text(r.autor || "-", 16, y); y += 6;
    });
  }

  doc.save(`prontuario_${(residente?.nome || "residente").replace(/\s+/g, "_")}_${dia}.pdf`);
}

export function gerarAnotacoesPeriodoPDF({ empresa, residente, registros, inicio, fim }) {
  const doc = new jsPDF();
  cabecalho(doc, empresa, "RELATÓRIO DE ANOTAÇÕES DE ENFERMAGEM",
    `Período: ${dataBR(inicio)} a ${dataBR(fim)}`);
  let y = 34;
  y = paciente(doc, y, residente);

  const filtradas = (registros || [])
    .filter((r) => r.tipo === "anotacao_enfermagem")
    .filter((r) => {
      const d = soData(r.data);
      return d >= inicio && d <= fim;
    })
    .sort((a, b) => (a.data || "").localeCompare(b.data || ""));

  if (filtradas.length === 0) {
    doc.setFont("helvetica", "italic"); doc.setFontSize(10); doc.setTextColor(148, 163, 184);
    doc.text("Nenhuma anotação de enfermagem no período selecionado.", 14, y);
    doc.save(`anotacoes_${(residente?.nome || "residente").replace(/\s+/g, "_")}.pdf`);
    return;
  }

  // Agrupa por dia
  const porDia = {};
  filtradas.forEach((r) => { (porDia[soData(r.data)] ||= []).push(r); });

  Object.keys(porDia).sort().forEach((dia) => {
    y = secao(doc, y, dataBR(dia));
    porDia[dia].forEach((r) => {
      const d = r.dados || {};
      const titulo = `Turno ${d.turno || "-"} · ${hora(r.data)}`;
      const linhas = doc.splitTextToSize(d.texto || "", 178);
      const bloco = 6 + linhas.length * 4.4 + 5;
      if (y + bloco > 285) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(37, 99, 235);
      doc.text(titulo, 16, y); y += 4.6;
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(51, 65, 85);
      doc.text(linhas, 16, y); y += linhas.length * 4.4 + 1;
      doc.setFontSize(7.5); doc.setTextColor(148, 163, 184);
      doc.text(r.autor || "-", 16, y); y += 6;
    });
  });

  doc.save(`anotacoes_${(residente?.nome || "residente").replace(/\s+/g, "_")}_${inicio}_${fim}.pdf`);
}
